import React from 'react';
import { Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReloadIconButton } from '../../elements';
import useAnimalFeedHistory from '../../hooks/useAnimalFeedHistory';
import {
  DEFAULT_ANIMAL_PERIOD,
  FeedPeriod,
  feedPeriodCoversDay,
  formatFeedAmount
} from '../../models/feed-registration';
import AnimalFeedHistoryTable from './AnimalFeedHistoryTable';
import FeedDailyChart from './FeedDailyChart';
import FeedPeriodSelect from './FeedPeriodSelect';

export interface AnimalFeedTabProps {
  animalId: string;
}

/**
 * Per-animal feed history: this animal's share of every feeding the pens it lived in received.
 *
 * The share is not stored anywhere - the backend divides each pen feeding by the piglets that pen
 * held on the day, reading the animal's location history. So the figures here move when an animal is
 * fostered in or out, and this view has nothing to invalidate or recalculate.
 *
 * Opens on the whole history rather than a look-back window. An animal lives a few months and only
 * earns a share on days its pen was fed, so the full list is short - and unlike a room, whose
 * interest really is "what happened this week", the question asked of one animal is usually how much
 * it has had in total.
 *
 * Read-only, like the per-location views: feed is registered from the mobile app during the feeding
 * round.
 */
const AnimalFeedTab: React.FC<AnimalFeedTabProps> = ({ animalId }) => {
  const { t } = useTranslation();
  const [period, setPeriod] = React.useState<FeedPeriod>(DEFAULT_ANIMAL_PERIOD);
  const { items, isBusy, loaded, reload } = useAnimalFeedHistory(animalId);

  // The endpoint is unpaged and takes no date bounds - it returns the whole history in one go - so
  // narrowing to a period is a filter here rather than another request.
  const shown = React.useMemo(
    () => (period.kind === 'all' ? items : items.filter((item) => feedPeriodCoversDay(period, item.date))),
    [items, period]
  );

  // Summed from what is on screen, not taken from the response: the response total covers the whole
  // history, so reusing it under a narrowed period would contradict the rows beneath it.
  const totalGrams = React.useMemo(() => shown.reduce((sum, item) => sum + item.allocatedGrams, 0), [shown]);
  const chartEntries = React.useMemo(
    () => shown.map((item) => ({ date: item.date, feedName: item.feedName, grams: item.allocatedGrams })),
    [shown]
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-gray-900 m-0">{t('f-feed-reg-animal-title')}</h2>
        <div className="flex items-center gap-3">
          {loaded && shown.length > 0 && (
            <>
              <Chip
                size="small"
                label={t('f-feed-reg-count', { count: shown.length })}
                className="!bg-gray-100 !text-gray-700"
              />
              <Chip
                size="small"
                label={t('f-feed-reg-piglet-total', {
                  amount: formatFeedAmount(totalGrams),
                  defaultValue: 'Piglet share total: {{amount}}'
                })}
                className="!bg-green-100 !text-green-800"
              />
            </>
          )}
          <FeedPeriodSelect value={period} onChange={setPeriod} disabled={isBusy} />
          <ReloadIconButton action={reload} />
        </div>
      </div>

      {/* Chart only once the rows are in: an axis built from a partial series would rescale as the
          rest arrives. It renders nothing when there is no data, leaving the table's empty state. */}
      {loaded && shown.length > 0 && (
        <div className="border-b border-gray-100">
          <FeedDailyChart
            entries={chartEntries}
            title={t('f-feed-reg-per-piglet-day', { defaultValue: 'Feed per piglet per day' })}
            formatDayTotal={(amount) =>
              t('f-feed-reg-daily-share', { amount, defaultValue: 'Daily share: {{amount}}' })
            }
          />
        </div>
      )}

      <div className="p-2">
        <AnimalFeedHistoryTable items={shown} isBusy={isBusy} loaded={loaded} />
      </div>
    </div>
  );
};

export default AnimalFeedTab;
